'use client';

import React, { useEffect, useId, useRef, useState } from 'react';
import { cn } from '@/core/utils/cn';
import { parseRasterLottie, type RasterLottieAnimation } from './lottie-raster';

// The legacy header renders its logo twice (desktop + mobile bars) — without this, every
// instance fetched the same animation JSON on its own (kidlox.fun: 135 KB downloaded twice on
// every page view).
const animationDataCache = new Map<string, Promise<any>>();

function fetchAnimationData(url: string): Promise<any> {
  let pending = animationDataCache.get(url);
  if (!pending) {
    pending = fetch(url).then((res) => {
      if (!res.ok) throw new Error(`HTTP error ${res.status}`);
      return res.json();
    });
    pending.catch(() => animationDataCache.delete(url));
    animationDataCache.set(url, pending);
  }
  return pending;
}

interface LottiePlayerProps {
  src: string | Record<string, any>;
  autoplay?: boolean;
  loop?: boolean;
  className?: string;
  style?: React.CSSProperties;
  ariaLabel?: string;
}

export function LottiePlayer({
  src,
  autoplay = true,
  loop = true,
  className,
  style,
  ariaLabel = 'Animacja Lottie',
}: LottiePlayerProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const animRef = useRef<any>(null);
  const [loadError, setLoadError] = useState(false);
  const [rasterAnimation, setRasterAnimation] = useState<RasterLottieAnimation | null>(null);
  const animationId = `raster-lottie-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;

  useEffect(() => {
    let isMounted = true;
    setLoadError(false);
    setRasterAnimation(null);

    const initLottie = async () => {
      try {
        // Clean up previous instance if any
        if (animRef.current) {
          animRef.current.destroy();
          animRef.current = null;
        }

        let animData = typeof src === 'object' ? src : null;
        let animPath = typeof src === 'string' ? src : undefined;

        if (typeof src === 'string') {
          const resolvedSrc = src.startsWith('http') || src.startsWith('/') ? src : `/${src}`;
          try {
            // lottie-web mutates animationData while loading it — each instance needs its own copy.
            animData = structuredClone(await fetchAnimationData(resolvedSrc));
            animPath = undefined;
          } catch (fetchErr) {
            console.warn('Lottie direct fetch error, falling back to path:', fetchErr);
            animPath = resolvedSrc;
          }
        }

        if (!containerRef.current || !isMounted) return;

        const raster = animData ? parseRasterLottie(animData) : null;
        if (raster) {
          setRasterAnimation(raster);
          return;
        }

        // Complex/vector animations still use the smallest official Lottie
        // build. The import happens only after the image-only CSS fast path was
        // rejected, so simple animated logos never download this chunk.
        const lottieModule = await import('lottie-web/build/player/lottie_light');
        const lottie = lottieModule.default || lottieModule;
        if (!containerRef.current || !isMounted) return;

        const animConfig: any = {
          container: containerRef.current,
          renderer: 'svg',
          loop: loop,
          autoplay: autoplay,
          rendererSettings: {
            preserveAspectRatio: 'xMidYMid meet',
            progressiveLoad: true,
            hideOnTransparent: true,
          },
        };

        if (animData) {
          animConfig.animationData = animData;
        } else if (animPath) {
          animConfig.path = animPath;
        }

        animRef.current = lottie.loadAnimation(animConfig);
      } catch (err) {
        console.error('LottiePlayer error:', err);
        if (isMounted) setLoadError(true);
      }
    };

    initLottie();

    return () => {
      isMounted = false;
      if (animRef.current) {
        animRef.current.destroy();
        animRef.current = null;
      }
    };
  }, [src, loop, autoplay]);

  if (loadError) {
    return null;
  }

  const rasterCss = rasterAnimation
    ? rasterAnimation.layers.map((layer, index) => {
        const frames = layer.opacityStops.map((stop) => `${(stop.offset * 100).toFixed(4)}% { opacity: ${stop.value}; }`).join(' ');
        return `@keyframes ${animationId}-${index} { ${frames} }`;
      }).join('\n')
    : '';

  return (
    <div
      ref={containerRef}
      role="img"
      aria-label={ariaLabel}
      className={cn('relative inline-flex items-center justify-center overflow-hidden', className)}
      style={{
        width: '100%',
        height: '100%',
        ...(rasterAnimation ? { aspectRatio: `${rasterAnimation.width} / ${rasterAnimation.height}` } : {}),
        ...style,
      }}
    >
      {rasterAnimation && (
        <>
          <style>{`${rasterCss}\n@media (prefers-reduced-motion: reduce) { [data-raster-lottie="${animationId}"] { animation: none !important; } }`}</style>
          {rasterAnimation.layers.map((layer, index) => (
            <img
              key={`${layer.zIndex}-${index}`}
              src={layer.src}
              alt=""
              aria-hidden="true"
              data-raster-lottie={animationId}
              className="pointer-events-none absolute inset-0 h-full w-full object-contain"
              style={{
                zIndex: layer.zIndex,
                clipPath: layer.clipPath,
                opacity: layer.initialOpacity,
                animationName: autoplay ? `${animationId}-${index}` : undefined,
                animationDuration: autoplay ? `${rasterAnimation.durationMs}ms` : undefined,
                animationIterationCount: autoplay ? (loop ? 'infinite' : 1) : undefined,
                animationTimingFunction: 'step-end',
                animationFillMode: 'forwards',
              }}
            />
          ))}
        </>
      )}
    </div>
  );
}
